import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../lib/auth';
import { format } from 'date-fns';
import { Loader2, Search, Check, CheckCircle2, XCircle, Clock, TrendingUp, Sparkles, UserCog, Ban, ChevronsUpDown } from 'lucide-react';

const fmtCurrency = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function UpsellsPage() {
  const { hasRole, user } = useAuth();
  const { toast } = useToast();
  const isAdmin = hasRole(['admin']);

  const [statusFilter, setStatusFilter] = useState(isAdmin ? 'pending_approval' : 'unassigned');
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pms, setPms] = useState([]);
  const [bulkPmPickerOpen, setBulkPmPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/upsells', { params: { status: statusFilter } });
      setData(res.data);
      setSelectedIds(new Set());
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Caricamento fallito', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);
  useEffect(() => { load(); }, [load]);

  // Load PMs list for admin reassign dropdown
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await api.get('/users', { params: { role: 'project_manager' } });
        setPms(res.data || []);
      } catch (err) { /* silent */ }
    })();
  }, [isAdmin]);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return data.items;
    const t = searchTerm.toLowerCase();
    return data.items.filter(i =>
      (i.client_name || '').toLowerCase().includes(t) ||
      (i.contract_number || '').toLowerCase().includes(t) ||
      (i.contract_name || '').toLowerCase().includes(t) ||
      (i.product_name || '').toLowerCase().includes(t) ||
      (i.upsell_assigned_pm_name || '').toLowerCase().includes(t)
    );
  }, [data.items, searchTerm]);

  const totalAmount = useMemo(() => filtered.reduce((s, i) => s + Number(i.value || 0), 0), [filtered]);
  const approvedCount = useMemo(() => filtered.filter(i => i.upsell_approved).length, [filtered]);
  const pendingCount = useMemo(() => filtered.filter(i => i.upsell_assigned_pm_id && !i.upsell_approved).length, [filtered]);

  // Selectable rows depend on current tab
  const isRowSelectable = useCallback((item) => {
    // Non admin (PM): only unassigned & not flagged as non_assignable items in unassigned tab
    if (!isAdmin) {
      return !item.upsell_assigned_pm_id && !item.upsell_non_assignable;
    }
    // Admin can select everything (non_assignable too — to mark/unmark)
    return true;
  }, [isAdmin]);

  const selectableRows = useMemo(
    () => filtered.filter(isRowSelectable),
    [filtered, isRowSelectable]
  );
  const allSelectable = selectableRows.length > 0 && selectableRows.every(r => selectedIds.has(r.contract_product_id));
  const someSelectable = selectableRows.some(r => selectedIds.has(r.contract_product_id));

  const selectedItems = useMemo(
    () => filtered.filter(i => selectedIds.has(i.contract_product_id)),
    [filtered, selectedIds]
  );
  const selectedCount = selectedItems.length;

  // Capability flags for bulk actions
  const canBulkAssign = selectedItems.some(i => !i.upsell_assigned_pm_id && !i.upsell_non_assignable);
  const canBulkApprove = isAdmin && selectedItems.some(i => i.upsell_assigned_pm_id && !i.upsell_approved);
  const canBulkUnassign = selectedItems.some(i => i.upsell_assigned_pm_id);
  const canBulkSetNonAssign = isAdmin && selectedItems.some(i => !i.upsell_non_assignable);
  const canBulkClearNonAssign = isAdmin && selectedItems.some(i => i.upsell_non_assignable);

  const toggleSelectAll = () => {
    if (allSelectable) {
      const next = new Set(selectedIds);
      selectableRows.forEach(r => next.delete(r.contract_product_id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      selectableRows.forEach(r => next.add(r.contract_product_id));
      setSelectedIds(next);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const doAssign = async (item, pmUserId = null) => {
    const key = pmUserId ? `reassign-${pmUserId}` : 'assign';
    setActionLoading(prev => ({ ...prev, [item.contract_product_id]: key }));
    try {
      const body = pmUserId ? { pm_user_id: pmUserId } : {};
      await api.post(`/contracts/${item.contract_id}/products/${item.contract_product_id}/upsell-assign`, body);
      toast({
        title: pmUserId
          ? 'Up-sell assegnato al PM selezionato'
          : (isAdmin ? 'Up-sell assegnato e approvato' : 'Up-sell richiesto'),
        description: !isAdmin && !pmUserId ? 'In attesa di approvazione admin' : '',
      });
      load();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setActionLoading(prev => ({ ...prev, [item.contract_product_id]: null }));
    }
  };

  const doApprove = async (item) => {
    setActionLoading(prev => ({ ...prev, [item.contract_product_id]: 'approve' }));
    try {
      await api.post(`/contracts/${item.contract_id}/products/${item.contract_product_id}/upsell-approve`);
      toast({ title: 'Approvato' });
      load();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setActionLoading(prev => ({ ...prev, [item.contract_product_id]: null }));
    }
  };

  const doUnassign = async (item) => {
    const isAdminReject = isAdmin && item.upsell_assigned_pm_id && !item.upsell_approved;
    const msg = isAdminReject
      ? `Rifiutare il claim di ${item.upsell_assigned_pm_name || 'questo PM'} su questo prodotto?`
      : 'Rimuovere l\'assegnazione di questo up-sell?';
    if (!window.confirm(msg)) return;
    setActionLoading(prev => ({ ...prev, [item.contract_product_id]: 'unassign' }));
    try {
      await api.delete(`/contracts/${item.contract_id}/products/${item.contract_product_id}/upsell-assign`);
      toast({ title: isAdminReject ? 'Claim rifiutato' : 'Assegnazione rimossa' });
      load();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setActionLoading(prev => ({ ...prev, [item.contract_product_id]: null }));
    }
  };

  const doSetNonAssignable = async (item, value) => {
    setActionLoading(prev => ({ ...prev, [item.contract_product_id]: value ? 'mark-na' : 'unmark-na' }));
    try {
      await api.post(`/contracts/${item.contract_id}/products/${item.contract_product_id}/upsell-non-assignable`, { value });
      toast({ title: value ? 'Marcato come non assegnabile' : 'Reso assegnabile' });
      load();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setActionLoading(prev => ({ ...prev, [item.contract_product_id]: null }));
    }
  };

  // ====== BULK ACTIONS ======
  const bulkRun = async (label, predicate, fn) => {
    const items = selectedItems.filter(predicate);
    if (items.length === 0) {
      toast({ title: 'Nessun elemento idoneo nella selezione' });
      return;
    }
    if (!window.confirm(`${label} su ${items.length} elementi selezionati?`)) return;
    setBulkLoading(true);
    let ok = 0, ko = 0, lastErr = '';
    for (const it of items) {
      try { await fn(it); ok++; } catch (e) { ko++; lastErr = e?.response?.data?.detail || e?.message || 'Errore'; }
    }
    setBulkLoading(false);
    toast({
      title: ko === 0 ? `${label}: ${ok} OK` : `${label}: ${ok} OK, ${ko} errori`,
      description: ko > 0 ? `Ultimo errore: ${lastErr}` : '',
      variant: ko === 0 ? 'default' : 'destructive',
    });
    load();
  };

  const bulkAssignTo = (pmUserId) => bulkRun(
    isAdmin && pmUserId !== user?.id ? 'Assegna al PM' : 'Assegna a me',
    i => !i.upsell_assigned_pm_id && !i.upsell_non_assignable,
    (it) => api.post(`/contracts/${it.contract_id}/products/${it.contract_product_id}/upsell-assign`, isAdmin && pmUserId ? { pm_user_id: pmUserId } : {})
  );

  const bulkApprove = () => bulkRun(
    'Approva',
    i => i.upsell_assigned_pm_id && !i.upsell_approved,
    (it) => api.post(`/contracts/${it.contract_id}/products/${it.contract_product_id}/upsell-approve`)
  );

  const bulkUnassign = () => bulkRun(
    'Rimuovi assegnazione',
    i => i.upsell_assigned_pm_id,
    (it) => api.delete(`/contracts/${it.contract_id}/products/${it.contract_product_id}/upsell-assign`)
  );

  const bulkSetNonAssignable = (value) => bulkRun(
    value ? 'Marca non assegnabile' : 'Rendi assegnabile',
    i => !!i.upsell_non_assignable !== value, // pick items needing the flip
    (it) => api.post(`/contracts/${it.contract_id}/products/${it.contract_product_id}/upsell-non-assignable`, { value })
  );

  const renderStatus = (item) => {
    if (item.upsell_non_assignable) {
      return <Badge variant="outline" className="text-gray-700 border-gray-400 bg-gray-100"><Ban className="w-3 h-3 mr-1" />Non assegnabile</Badge>;
    }
    if (!item.upsell_assigned_pm_id) {
      return <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50"><Clock className="w-3 h-3 mr-1" />Da assegnare</Badge>;
    }
    if (item.upsell_approved) {
      return <Badge variant="default" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1" />Approvato</Badge>;
    }
    return <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50"><Sparkles className="w-3 h-3 mr-1" />In attesa</Badge>;
  };

  // Inline PM assign dropdown (admin)
  const AssignPMSelect = ({ item, label }) => {
    const al = actionLoading[item.contract_product_id];
    const isLoading = typeof al === 'string' && al.startsWith('reassign-');
    return (
      <Select value="" onValueChange={(pmId) => doAssign(item, pmId)} disabled={!!al || item.upsell_non_assignable}>
        <SelectTrigger className="h-7 text-xs w-[130px] px-2" data-testid={`upsell-reassign-${item.contract_product_id}`}>
          {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : (
            <span className="flex items-center gap-1"><UserCog className="w-3 h-3" /> {label}</span>
          )}
        </SelectTrigger>
        <SelectContent>
          {pms.filter(p => p.id !== item.upsell_assigned_pm_id).map(p => (
            <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>
          ))}
          {pms.length === 0 && (
            <div className="px-2 py-1 text-xs text-gray-400">Nessun PM disponibile</div>
          )}
        </SelectContent>
      </Select>
    );
  };

  // Bulk PM picker (admin)
  const BulkPMPicker = () => (
    <Popover open={bulkPmPickerOpen} onOpenChange={setBulkPmPickerOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={bulkLoading || !canBulkAssign}
          data-testid="upsell-bulk-assign-trigger"
        >
          <UserCog className="w-3 h-3 mr-1" /> Assegna a PM...
          <ChevronsUpDown className="w-3 h-3 ml-1 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder="Cerca PM..." />
          <CommandList>
            <CommandEmpty>Nessun PM trovato.</CommandEmpty>
            <CommandGroup>
              {pms.map((p) => (
                <CommandItem
                  key={p.id}
                  value={p.name}
                  onSelect={() => { setBulkPmPickerOpen(false); bulkAssignTo(p.id); }}
                  className="text-xs"
                  data-testid={`upsell-bulk-assign-pm-${p.id}`}
                >
                  {p.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  const showCheckboxColumn = true; // Always show checkboxes (PM can also bulk-assign to self)
  const tabsForAdmin = ['pending_approval', 'unassigned', 'approved', 'non_assignable', 'mine', 'all'];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-orange-500" />
            Up-Sell
          </h1>
          <p className="text-sm text-gray-500">
            {isAdmin
              ? 'Gestisci le richieste di up-sell dei PM: approva, rifiuta, riassegna o marca come non assegnabili.'
              : 'Rivendica gli up-sell sui tuoi clienti. Le richieste saranno approvate dall\'admin.'}
          </p>
        </div>
      </div>

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList>
          {!isAdmin && <TabsTrigger value="unassigned" data-testid="tab-upsell-unassigned">Da Assegnare</TabsTrigger>}
          <TabsTrigger value="mine" data-testid="tab-upsell-mine">I Miei</TabsTrigger>
          {isAdmin && <TabsTrigger value="pending_approval" data-testid="tab-upsell-pending">In Attesa Approvazione</TabsTrigger>}
          {isAdmin && <TabsTrigger value="approved" data-testid="tab-upsell-approved">Approvati</TabsTrigger>}
          {isAdmin && <TabsTrigger value="unassigned" data-testid="tab-upsell-unassigned-admin">Non Assegnati</TabsTrigger>}
          {isAdmin && <TabsTrigger value="non_assignable" data-testid="tab-upsell-non-assignable">Non Assegnabili</TabsTrigger>}
          {isAdmin && <TabsTrigger value="all" data-testid="tab-upsell-all">Tutti</TabsTrigger>}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="relative max-w-sm flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Cerca contratto, cliente, prodotto, PM..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9"
                data-testid="upsell-search"
              />
            </div>
            <div className="flex gap-4 text-xs text-gray-600">
              <span><b className="text-gray-900">{filtered.length}</b> totali</span>
              {pendingCount > 0 && <span><b className="text-blue-700">{pendingCount}</b> in attesa</span>}
              {approvedCount > 0 && <span><b className="text-emerald-700">{approvedCount}</b> approvati</span>}
              <span>Valore totale: <b className="text-gray-900">{fmtCurrency(totalAmount)}</b></span>
            </div>
          </div>

          {/* Bulk actions toolbar */}
          {selectedCount > 0 && (
            <div className="mt-3 flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md flex-wrap">
              <span className="text-xs text-blue-900 font-medium">
                {selectedCount} selezionati
              </span>
              <div className="flex-1" />

              {/* PM: Assegna a me */}
              {!isAdmin && canBulkAssign && (
                <Button
                  size="sm"
                  className="h-7 text-xs bg-orange-500 hover:bg-orange-600"
                  onClick={() => bulkAssignTo(null)}
                  disabled={bulkLoading}
                  data-testid="upsell-bulk-assign-self"
                >
                  {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><UserCog className="w-3 h-3 mr-1" />Assegna a me</>}
                </Button>
              )}

              {/* Admin: Assegna a PM specifico */}
              {isAdmin && canBulkAssign && <BulkPMPicker />}

              {/* Approva selezionati (admin) */}
              {canBulkApprove && (
                <Button
                  size="sm"
                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                  onClick={bulkApprove}
                  disabled={bulkLoading}
                  data-testid="upsell-bulk-approve"
                >
                  {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Approva</>}
                </Button>
              )}

              {/* Rimuovi assegnazione selezionati */}
              {canBulkUnassign && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                  onClick={bulkUnassign}
                  disabled={bulkLoading}
                  data-testid="upsell-bulk-unassign"
                >
                  <XCircle className="w-3 h-3 mr-1" />Rimuovi assegnazione
                </Button>
              )}

              {/* Admin: marca / smarca non assegnabile */}
              {canBulkSetNonAssign && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-gray-700 border-gray-400 hover:bg-gray-50"
                  onClick={() => bulkSetNonAssignable(true)}
                  disabled={bulkLoading}
                  data-testid="upsell-bulk-mark-na"
                >
                  <Ban className="w-3 h-3 mr-1" />Marca non assegnabile
                </Button>
              )}
              {canBulkClearNonAssign && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => bulkSetNonAssignable(false)}
                  disabled={bulkLoading}
                  data-testid="upsell-bulk-clear-na"
                >
                  <Check className="w-3 h-3 mr-1" />Rendi assegnabile
                </Button>
              )}

              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => setSelectedIds(new Set())}
                disabled={bulkLoading}
                data-testid="upsell-bulk-clear"
              >
                Annulla
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-0 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-500">Nessun up-sell trovato.</div>
          ) : (
            <Table data-testid="upsells-table">
              <TableHeader>
                <TableRow className="bg-gray-50">
                  {showCheckboxColumn && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelectable ? true : (someSelectable ? 'indeterminate' : false)}
                        onCheckedChange={toggleSelectAll}
                        disabled={selectableRows.length === 0}
                        data-testid="upsell-select-all"
                      />
                    </TableHead>
                  )}
                  <TableHead className="text-xs">Contratto</TableHead>
                  <TableHead className="text-xs">Cliente</TableHead>
                  <TableHead className="text-xs">Prodotto</TableHead>
                  <TableHead className="text-xs">BU</TableHead>
                  <TableHead className="text-xs">Firma</TableHead>
                  <TableHead className="text-xs text-right">Valore</TableHead>
                  <TableHead className="text-xs">Stato</TableHead>
                  <TableHead className="text-xs">Assegnato a</TableHead>
                  <TableHead className="text-xs text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((item) => {
                  const cpid = item.contract_product_id;
                  const al = actionLoading[cpid];
                  const canSelect = isRowSelectable(item);
                  return (
                    <TableRow key={cpid} data-testid={`upsell-row-${cpid}`} className={item.upsell_non_assignable ? 'bg-gray-50/50' : ''}>
                      {showCheckboxColumn && (
                        <TableCell>
                          {canSelect ? (
                            <Checkbox
                              checked={selectedIds.has(cpid)}
                              onCheckedChange={() => toggleSelect(cpid)}
                              data-testid={`upsell-select-${cpid}`}
                            />
                          ) : null}
                        </TableCell>
                      )}
                      <TableCell className="text-xs">
                        <div className="font-medium">{item.contract_number || '-'}</div>
                        <div className="text-gray-400">{item.contract_name || ''}</div>
                      </TableCell>
                      <TableCell className="text-xs">{item.client_name || '-'}</TableCell>
                      <TableCell className="text-xs">{item.product_name || '-'}</TableCell>
                      <TableCell className="text-xs text-gray-500">{item.business_unit_name || '-'}</TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {item.signing_date ? format(new Date(item.signing_date), 'dd/MM/yy') : '-'}
                      </TableCell>
                      <TableCell className="text-xs text-right font-medium">{fmtCurrency(item.value)}</TableCell>
                      <TableCell>{renderStatus(item)}</TableCell>
                      <TableCell className="text-xs">{item.upsell_assigned_pm_name || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5 items-center flex-wrap">
                          {/* Assegna a me (PM o admin che vuole assegnare a se stesso) */}
                          {!item.upsell_assigned_pm_id && !item.upsell_non_assignable && (
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => doAssign(item)} disabled={!!al}
                              data-testid={`upsell-assign-${cpid}`}>
                              {al === 'assign' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Assegna a me'}
                            </Button>
                          )}
                          {/* Admin: Assegna direttamente ad altro PM (dropdown) */}
                          {isAdmin && !item.upsell_non_assignable && (
                            <AssignPMSelect item={item} label={item.upsell_assigned_pm_id ? 'Riassegna' : 'Assegna a...'} />
                          )}
                          {/* Approva (admin) */}
                          {isAdmin && item.upsell_assigned_pm_id && !item.upsell_approved && (
                            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => doApprove(item)} disabled={!!al}
                              data-testid={`upsell-approve-${cpid}`}>
                              {al === 'approve' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Approva</>}
                            </Button>
                          )}
                          {/* Rimuovi assegnazione */}
                          {item.upsell_assigned_pm_id && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => doUnassign(item)} disabled={!!al}
                              data-testid={`upsell-unassign-${cpid}`}>
                              {al === 'unassign' ? <Loader2 className="w-3 h-3 animate-spin" /> : (
                                <><XCircle className="w-3 h-3 mr-1" />{isAdmin && !item.upsell_approved ? 'Rifiuta' : 'Rimuovi'}</>
                              )}
                            </Button>
                          )}
                          {/* Admin: marca non assegnabile */}
                          {isAdmin && !item.upsell_non_assignable && !item.upsell_assigned_pm_id && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-600 hover:bg-gray-100"
                              onClick={() => doSetNonAssignable(item, true)} disabled={!!al}
                              data-testid={`upsell-mark-na-${cpid}`}>
                              {al === 'mark-na' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Ban className="w-3 h-3 mr-1" />Non assegnabile</>}
                            </Button>
                          )}
                          {isAdmin && item.upsell_non_assignable && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs"
                              onClick={() => doSetNonAssignable(item, false)} disabled={!!al}
                              data-testid={`upsell-unmark-na-${cpid}`}>
                              {al === 'unmark-na' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Rendi assegnabile</>}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
